import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import customerService from "../Services/customerService";
import { LoadingDots } from "../Components/LoadingDots";
import { FiEdit3 } from "react-icons/fi";

const CustomerDetailsEdit = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [responseError, setResponseError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    designation: "",
    company_name: "",
    company_type: "",
    office_address: "",
    warehouse_address: "",
    rep_name: "",
    rep_phone: "",
    rep_email: "",
    reference: "",
    trn: "",
    terms: "",
    country: "",
    otp: "",
    token: "",
  });

  const [errors, setErrors] = useState({
    first_name: false,
    last_name: false,
    email: false,
    phone_number: false,
    designation: false,
    company_name: false,
    company_type: false,
    office_address: false,
    warehouse_address: false,
    rep_name: false,
    rep_phone: false,
    rep_email: false,
    reference: false,
    trn: false,
    terms: false,
    country: false,
    otp: false,
    token: false,
  });

  const [regex, setRegex] = useState({
    first_name: /.{1,50}$/,
    last_name: /.{1,50}$/,
    email: /^[\w\.\-]+@[a-zA-Z\d\-]+\.[a-zA-Z]{2,}$/,
    phone_number: /^\+?[0-9]{7,15}$/,
    designation: /.{1,50}$/,
    company_name: /.{1,50}$/,
    company_type: /.{1,50}$/,
    office_address: /.{1,50}$/,
    warehouse_address: /.{1,50}$/,
    rep_name: /.{1,50}$/,
    rep_phone: /^\+?[0-9]{7,15}$/,
    rep_email: /.{1,50}$/,
    reference: /.{1,50}$/,
    trn: /^\+?[0-9]{1,100}$/,
    terms: /.{1,50}$/,
    country: /.{1,50}$/,
    otp: /.{1,50}$/,
    token: /.{1,50}$/,
  });

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setLoading(true);
        const response = await customerService.fetchById(id);
        if (response && response.customer) {
          setData(response.customer);
        } else {
          toast.error("No customer data found");
        }
        setLoading(false);
      } catch (error) {
        toast.error(error.message || "Error fetching customer data");
        setLoading(false);
      }
    };

    if (id) {
      fetchCustomerData();
    } else {
      toast.error("No customer ID provided");
      navigate("/customers");
    }
  }, [id, navigate]);

  const handleChange = (v, n) => {
    setResponseError("");
    setData((d) => ({ ...d, [n]: v }));
    if (v.length > 0) {
      setErrors((prevState) => ({ ...prevState, [n]: false }));
    }
  };

  const handleSubmit = () => {
    let hasError = false;

    Object.keys(data).forEach((field) => {
      if (regex[field] && !regex[field].test(data[field])) {
        setErrors((prevState) => ({ ...prevState, [field]: true }));
        hasError = true;
      }
    });

    if (hasError) {
      toast.error("Please check the format of your inputs", { id: "1" });
      return;
    }

    setLoading(true);

    const updateData = {};
    Object.keys(data).forEach((key) => {
      if (data[key] !== undefined && data[key] !== null) {
        updateData[key] = data[key];
      }
    });
    console.log(updateData);
    customerService
      .updateProfile(id, updateData)
      .then((res) => {
        setLoading(false);
        toast.success("Customer Updated Successfully", { id: "2" });
        navigate("/customers");
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err.message || "Error updating customer");
      });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <main className="mx-10 my-2 p-6 rounded-3xl border border-gray-200 shadow-lg min-h-[calc(100vh-48px)] bg-white">
      <h1 className="font-bold text-center text-3xl text-secondary mt-10">
        Edit Customer
      </h1>

      <div className="mt-10 px-80">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4">
          <div>
            <label className={`primary-label`}>First Name</label>
            <input
              value={data.first_name}
              name="first_name"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="First Name"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["first_name"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
          <div>
            <label className={`primary-label`}>Last Name</label>
            <input
              value={data.last_name}
              name="last_name"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Last Name"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["last_name"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
          <div>
            <label className={`primary-label`}>Email</label>
            <input
              value={data.email}
              name="email"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Email"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["email"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
          <div>
            <label className={`primary-label`}>Designation</label>
            <input
              value={data.designation}
              name="designation"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Designation"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["designation"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 md:grid-cols-5 md:gap-4 mt-4">
          <div>
            <label className={`primary-label`}>Phone Number</label>
            <input
              value={data.phone_number}
              name="phone_number"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Phone Number"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["phone_number"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>

          <div className="col-span-2">
            <label className={`primary-label`}>Company Name</label>
            <input
              value={data.company_name}
              name="company_name"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Company Name"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["company_name"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
          <div className="col-span-2">
            <label className={`primary-label`}>Company Type</label>
            <input
              value={data.company_type}
              name="company_type"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Company Type"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["company_type"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 md:grid-cols-2 md:gap-4 mt-4">
          <div>
            <label className={`primary-label`}>Office Address</label>
            <input
              value={data.office_address}
              name="office_address"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Office Address"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["office_address"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
          <div>
            <label className={`primary-label`}>Warehouse Address</label>
            <input
              value={data.warehouse_address}
              name="warehouse_address"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Warehouse Address"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["warehouse_address"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-4 mt-4">
          <div>
            <label className={`primary-label`}>Representative Name</label>
            <input
              value={data.rep_name}
              name="rep_name"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Representative Name"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["rep_name"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
          <div>
            <label className={`primary-label`}>TRN #</label>
            <input
              value={data.trn}
              name="trn"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="TRN"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["trn"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
          <div>
            <label className={`primary-label`}>Rep Phone</label>
            <input
              value={data.rep_phone}
              name="rep_phone"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Rep Phone"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["rep_phone"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 md:grid-cols-2 md:gap-4 mt-4">
          <div>
            <label className={`primary-label`}>Rep Email</label>
            <input
              value={data.rep_email}
              name="rep_email"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Rep Email"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["rep_email"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
          <div>
            <label className={`primary-label`}>Reference</label>
            <input
              value={data.reference}
              name="reference"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Reference"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["reference"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4 mt-4">
          <div>
            <label className={`primary-label`}>Terms</label>
            <input
              value={data.terms}
              name="terms"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Terms"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["terms"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
          <div>
            <label className={`primary-label`}>Country</label>
            <input
              value={data.country}
              name="country"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Country"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["country"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
          <div>
            <label className={`primary-label`}>OTP</label>
            <input
              value={data.otp}
              name="otp"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="OTP"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["otp"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
          <div>
            <label className={`primary-label`}>Token</label>
            <input
              value={data.token}
              name="token"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Token"
              onKeyDown={handleKeyDown}
              className={`primary-input ${errors["token"] ? "border-red-600" : "border-gray-300"}`}
            />
          </div>
        </div>

        <div>
          {responseError ? (
            <div className="text-red-600 text-sm">{responseError}</div>
          ) : (
            ""
          )}
        </div>

        <div className="flex items-center justify-center flex-col mt-12">
          <button
            onClick={() => handleSubmit()}
            disabled={loading}
            className={`bg-white text-black border font-medium text-xs px-12 py-4 rounded-lg shadow-sm hover:shadow-lg duration-300 transition-all flex flex-row items-center overflow-hidden group ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {loading ? (
              <LoadingDots />
            ) : (
              <>
                <FiEdit3 className="text-xl w-0 opacity-0 group-hover:w-6 group-hover:opacity-100 group-hover:mr-2 transition-all duration-300 ease-in-out" />
                <div>Edit</div>
              </>
            )}
          </button>
        </div>
      </div>
    </main>
  );
};

export default CustomerDetailsEdit;
